import React, { useState } from 'react';
import axios from 'axios';
import {
  Feather, BookOpen, Users, Lightbulb, Wand2, Headphones,
  Languages, Image as ImageIcon, BookMarked, ArrowRight, ExternalLink,
  Bell, CheckCircle, Loader2, Sparkles, Star, Flame, HeartHandshake, Calendar,
  Lock, Eye, MessageSquare, Send
} from 'lucide-react';
import ReviewsSection from './ReviewsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOK_AMAZON_URL = "https://www.amazon.com/Our-Darkest-Place-Gods-Grace/dp/B0G4R8WR18/";

const FEATURE_GRID = [
  { icon: BookOpen, title: "Manuscript Studio", blurb: "Bring your work in — DOCX, PDF, paste, or speak it aloud." },
  { icon: Sparkles, title: "Soulful Analysis", blurb: "AI that reflects what makes your voice unique, not what's wrong." },
  { icon: Users, title: "Character Builder", blurb: "Discover the people already living in your imagination." },
  { icon: Lightbulb, title: "Creative Brainstorm", blurb: "The messy, beautiful space where ideas are born." },
  { icon: Wand2, title: "Polish & Transform", blurb: "Refine your words until they sing the way you hear them." },
  { icon: Headphones, title: "Audiobook Creator", blurb: "Nine voices, chapter markers, KDP-ready audio." },
  { icon: Languages, title: "Translation", blurb: "Carry your story into 12 languages — soul intact." },
  { icon: ImageIcon, title: "Cover Art Studio", blurb: "KDP-ready covers, album art, poem visuals — print-quality." },
  { icon: BookMarked, title: "Publishing Path", blurb: "Query letters, publisher search, the road to readers." },
];

const LandingPage = ({ onEnterStudio }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [waitlistState, setWaitlistState] = useState({ status: 'idle', position: null, message: '' });

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setWaitlistState({ status: 'loading', position: null, message: '' });
    try {
      const r = await axios.post(`${API}/waitlist/join`, {
        email: email.trim(),
        name: name.trim(),
        source: 'scribeflow_landing'
      });
      setWaitlistState({
        status: 'success',
        position: r.data.position,
        message: r.data.already ? "You're already on the list — we've got you." : "Welcome to the founding circle."
      });
    } catch (err) {
      setWaitlistState({
        status: 'error',
        position: null,
        message: err.response?.data?.detail || 'Something went sideways. Try again?'
      });
    }
  };

  return (
    <div className="sf-landing" data-testid="scribeflow-landing">

      {/* ============ TOP BAR ============ */}
      <header className="sf-topbar">
        <div className="sf-topbar-inner">
          <a href="https://aiquires-platform-production.up.railway.app/" target="_blank" rel="noopener noreferrer" className="sf-parent-link" data-testid="parent-brand-link">
            <Flame className="sf-phoenix-mini" />
            <span className="sf-parent-text">AiQ Phoenix Tech</span>
          </a>
          <nav className="sf-topbar-nav">
            <div className="sf-nav-links">
              <a href="#origin">Story</a>
              <a href="#mission">The Promise</a>
              <a href="#inside">What's Inside</a>
              <a href="#how-it-works">Free + How It Works</a>
              <a href="#suite">The Phoenix Suite</a>
              <a href="#reviews">Reviews</a>
            </div>
            <div className="sf-nav-ctas">
              <a
                href="https://aiquires-platform-production.up.railway.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="sf-cta-btn sf-cta-btn-outline"
                data-testid="topbar-resume-optimizer"
              >
                Resume Optimizer <ExternalLink size={14} />
              </a>
              <button className="sf-cta-btn sf-cta-btn-primary" onClick={onEnterStudio} data-testid="enter-studio-top">
                Enter the Studio <ArrowRight size={16} />
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="sf-hero">
        <div className="sf-hero-glow" aria-hidden="true" />
        <div className="sf-hero-inner">
          <div className="sf-hero-eyebrow">
            <span className="sf-pill">AiQ Phoenix Create</span>
          </div>
          <h1 className="sf-hero-title">
            <span className="sf-h1-line-1">Scribe Flow</span>
            <span className="sf-h1-line-2">Studio</span>
          </h1>
          <p className="sf-hero-tag">Where Art Meets Words</p>
          <p className="sf-hero-sub">
            A sanctuary for memoirists, poets, songwriters, and creators of all kinds.
            Soulful AI companionship from the first spark to the printed page —
            and every uncertain hour in between.
          </p>
          <div className="sf-hero-ctas">
            <button className="sf-cta-btn sf-cta-btn-primary sf-cta-btn-lg" onClick={onEnterStudio} data-testid="enter-studio-hero">
              Enter the Studio <ArrowRight size={18} />
            </button>
            <a href="#origin" className="sf-cta-btn sf-cta-btn-ghost sf-cta-btn-lg">
              Hear the Story
            </a>
          </div>
          <p className="sf-hero-mini">
            <Feather size={14} />
            <span>Free to explore during launch • Founding-member pricing soon</span>
          </p>
        </div>
      </section>

      {/* ============ ORIGIN STORY ============ */}
      <section id="origin" className="sf-section sf-origin">
        <div className="sf-section-inner">
          <div className="sf-section-eyebrow">The Origin</div>
          <h2 className="sf-section-title sf-origin-title">
            <span>Built From the Trenches.</span>
            <span>Built With Purpose.</span>
            <span>Built So Your Creativity Can Rise.</span>
          </h2>

          <div className="sf-origin-body">
            <p className="sf-origin-lead">
              Scribe Flow Studio wasn't born from a single genre or a single style of writing.
              It was born from something deeper — the belief that <em>writing is an art</em>,
              and every form of it deserves support.
            </p>

            <p>
              Before Scribe Flow existed, I spent a year in a faith‑based recovery center,
              filling a journal with raw, honest entries that helped me process, heal, and rebuild.
              Those pages eventually became a book — but more importantly, they reminded me that
              writing, in any form, is one of the purest expressions of the human soul.
            </p>

            <p>
              When I sat down at an old Windows 10 all‑in‑one my father‑in‑law gave me,
              I wasn't just typing a manuscript.
            </p>

            <ul className="sf-origin-list">
              <li>I was stepping into vulnerability.</li>
              <li>I was facing fear of judgment.</li>
              <li>I was rediscovering purpose.</li>
            </ul>

            <p>
              And I was learning — quickly — that creators of all kinds face the same walls:
            </p>

            <div className="sf-origin-walls">
              <span>editing</span>
              <span>structure</span>
              <span>formatting</span>
              <span>covers</span>
              <span>translations</span>
              <span>audiobooks</span>
              <span>marketing</span>
              <span>and the emotional weight of doing it alone</span>
            </div>

            <p>
              Every obstacle I hit became a blueprint.<br/>
              Every frustration became a feature.<br/>
              Every "no" became a reason to build something better.
            </p>

            <p>
              And that's how Scribe Flow Studio was born — not as a tool for one type of writer,
              but as an <strong>ecosystem for every creator who uses words as their art</strong>.
            </p>

            <div className="sf-origin-subhead">
              <Star size={20} />
              <h3>A Studio for Every Kind of Writer</h3>
            </div>

            <p>
              From poems, lyrics, memoirs, to slam poetry and screenplays — and everything in
              between — writing is a form of artistic expression, and Scribe Flow Studio is your canvas.
            </p>

            <ul className="sf-origin-list">
              <li>A place where creators don't have to feel alone.</li>
              <li>A place where vulnerability isn't a weakness — it's the starting point.</li>
              <li>A place where the Phoenix mindset lives in the code:</li>
            </ul>

            <div className="sf-phoenix-creed">
              <Flame size={22} className="sf-creed-icon" />
              <div className="sf-creed-lines">
                <span>We never back down.</span>
                <span>We never take no for an answer.</span>
                <span>We always find a solution.</span>
                <span className="sf-creed-rise">We rise from the ashes.</span>
              </div>
            </div>

            <div className="sf-origin-built-for">
              <p className="sf-built-for-intro">Scribe Flow was built for —</p>
              <ul className="sf-built-for-list">
                <li><span>the creator</span> with passion but no editor</li>
                <li><span>the musician</span> with lyrics but no structure</li>
                <li><span>the teen in recovery</span> who needs a voice</li>
                <li><span>the writer</span> with a message but no roadmap</li>
                <li><span>the artist</span> who feels something powerful but doesn't know where to begin</li>
              </ul>
            </div>

            <p className="sf-origin-emphasis">
              This is a studio built from the trenches, built with purpose,
              and built so your creativity can rise.
            </p>
          </div>
        </div>
      </section>

      {/* ============ THE BOOK THAT SPARKED THE MISSION ============ */}
      <section id="the-book" className="sf-section sf-book">
        <div className="sf-section-inner sf-book-inner">
          <div className="sf-book-cover-wrap">
            <a
              href={BOOK_AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sf-book-cover-link"
              data-testid="book-cover-link"
            >
              <div className="sf-book-cover-placeholder" aria-label="Book cover artwork placeholder">
                <BookOpen size={72} />
              </div>
              <span className="sf-book-cover-shine" aria-hidden="true" />
            </a>
          </div>

          <div className="sf-book-text">
            <div className="sf-section-eyebrow">
              <Star size={14} /> The Book That Sparked the Mission
            </div>
            <h2 className="sf-section-title sf-book-title">
              <span className="sf-book-title-main">From Our Darkest Place</span>
              <span className="sf-book-title-sub">To God's Grace</span>
              <span className="sf-book-title-tag">When Love Rewrites the Story</span>
            </h2>
            <p className="sf-book-author">by Aaron Hubbard</p>
            <p className="sf-book-blurb">
              Before Scribe Flow Studio became an ecosystem for creators, it began with a journal —
              a year of handwritten entries that helped me rebuild my life and eventually became my book.
            </p>
            <p className="sf-book-blurb">
              A true account of recovery, faith, and redemption — and the project that revealed
              how many barriers creators face when trying to bring their work into the world.
            </p>
            <a
              href={BOOK_AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sf-cta-btn sf-cta-btn-primary sf-cta-btn-lg"
              data-testid="book-amazon-cta"
            >
              Read on Amazon <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ============ MISSION MONTH ============ */}
      <section id="mission" className="sf-section sf-mission">
        <div className="sf-section-inner sf-mission-inner">
          <div className="sf-mission-badge">
            <HeartHandshake size={16} />
            <span>The Scribe Flow Promise</span>
          </div>

          <h2 className="sf-section-title sf-mission-title">
            One month, every year — book royalties go back to the place that gives writers their voice.
          </h2>

          <p className="sf-mission-lede">
            Scribe Flow Studio was born inside the walls of a recovery center, where a journal
            became a lifeline. We never forget that. So every year, for one full month,
            <strong> 50% of the author's royalties from <em>From Our Darkest Place To God's Grace</em>
            — after Amazon's printing and shipping fees — are donated to a recovery center of the
            author's choosing</strong>, helping cover treatment costs for people fighting to find
            their own voice again.
          </p>

          <div className="sf-mission-card">
            <div className="sf-mission-card-left">
              <Calendar size={28} className="sf-mission-cal" />
              <div className="sf-mission-dates">
                <span className="sf-mission-month">May 1 — May 31</span>
                <span className="sf-mission-year">This Year</span>
              </div>
            </div>
            <div className="sf-mission-card-right">
              <p>
                Every book royalty earned on Amazon from May 1st through May 31st (after printing
                and shipping) will be donated <strong>50/50</strong> to a recovery center in the
                United States that is deeply personal to our founder — the place where this
                entire story began.
              </p>
              <p className="sf-mission-quiet">
                The center will be named publicly when the donation is delivered. Until then,
                we honor it in silence — the way it honored us. Want to help right now?
                <a href={BOOK_AMAZON_URL} target="_blank" rel="noopener noreferrer" className="sf-mission-inline-link"> Buy the book on Amazon ↗</a>
              </p>
            </div>
          </div>

          <p className="sf-mission-footer">
            Future years, future centers, future authors stepping forward to nominate a place
            that saved them. This is the Scribe Flow rhythm — create, give, rise.
          </p>
        </div>
      </section>

      {/* ============ INSIDE / FEATURES ============ */}
      <section id="inside" className="sf-section sf-inside">
        <div className="sf-section-inner">
          <div className="sf-section-eyebrow">What Lives Inside</div>
          <h2 className="sf-section-title">Nine doors. One studio.</h2>
          <p className="sf-section-sub">
            Walk through them in any order. Stay as long as you need. Your work is safe here.
          </p>

          <div className="sf-feature-grid" data-testid="feature-grid">
            {FEATURE_GRID.map((f, i) => (
              <div key={i} className="sf-feature-card">
                <div className="sf-feature-icon-wrap">
                  <f.icon size={22} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.blurb}</p>
              </div>
            ))}
          </div>

          <div className="sf-inside-cta">
            <button className="sf-cta-btn sf-cta-btn-primary sf-cta-btn-lg" onClick={onEnterStudio} data-testid="enter-studio-features">
              Step Inside <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ============ THE PHOENIX SUITE ============ */}
      <section id="suite" className="sf-section sf-suite">
        <div className="sf-section-inner">
          <div className="sf-suite-header">
            <div className="sf-section-eyebrow">The Phoenix Suite</div>
            <h2 className="sf-section-title">Pillars of Innovation</h2>
            <p className="sf-section-sub">
              Accessible, human-centered AI tools that solve real-world problems —
              no gimmicks, no fabrication.
            </p>
          </div>

          <div className="sf-suite-grid">

            {/* Scribe Flow (current) */}
            <div className="sf-suite-card sf-suite-card-current">
              <div className="sf-suite-status sf-status-here">You Are Here</div>
              <div className="sf-suite-logo-wrap sf-suite-logo-letter">
                <span>S</span>
              </div>
              <h3>Scribe Flow Studio</h3>
              <p className="sf-suite-cat">AiQ Phoenix Create</p>
              <ul>
                <li>AI-powered writing</li>
                <li>Document builder</li>
                <li>Audiobook & cover creation</li>
                <li>Translation & publishing path</li>
              </ul>
              <div className="sf-suite-price">Pricing <span>Founding access soon</span></div>
              <button className="sf-cta-btn sf-cta-btn-primary sf-cta-btn-block" onClick={onEnterStudio}>
                Enter the Studio <ArrowRight size={16} />
              </button>
            </div>

            {/* Resume Optimizer (live) */}
            <div className="sf-suite-card sf-suite-card-live">
              <div className="sf-suite-status sf-status-live">Live Now</div>
              <div className="sf-suite-logo-wrap sf-suite-logo-letter">
                <span>R</span>
              </div>
              <h3>Resume Optimizer</h3>
              <p className="sf-suite-cat">AiQ Phoenix Career Tools</p>
              <ul>
                <li>ATS-optimized resumes</li>
                <li>Tailored cover letters</li>
                <li>Interview prep guides</li>
                <li>Career narrative & strategy</li>
              </ul>
              <div className="sf-suite-price">Starting at <span>$9.99</span></div>
              <a
                href="https://aiquires-platform-production.up.railway.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="sf-cta-btn sf-cta-btn-primary sf-cta-btn-block"
                data-testid="resume-optimizer-cta"
              >
                Try Resume Optimizer <ExternalLink size={16} />
              </a>
            </div>

            {/* AiQ Phoenix MSP (coming soon) */}
            <div className="sf-suite-card sf-suite-card-soon">
              <div className="sf-suite-status sf-status-soon">Coming Soon</div>
              <div className="sf-suite-logo-wrap sf-suite-logo-letter">
                <span>M</span>
              </div>
              <h3>AiQ Phoenix MSP</h3>
              <p className="sf-suite-cat">IT Management & Automation</p>
              <ul>
                <li>Backup & recovery</li>
                <li>Network monitoring & alerts</li>
                <li>Endpoint security tools</li>
                <li>Maintenance automation</li>
              </ul>
              <div className="sf-suite-price">Subscription <span>TBD</span></div>
              <button className="sf-cta-btn sf-cta-btn-ghost sf-cta-btn-block" disabled>
                <Bell size={16} /> Notify Me
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* ============ FREE + BYOK EXPLAINER ============ */}
      <section id="how-it-works" className="sf-section sf-byok">
        <div className="sf-section-inner sf-byok-inner">
          <div className="sf-byok-headline">
            <div className="sf-free-pill">
              <Sparkles size={14} /> 100% Free During Launch
            </div>
            <h2 className="sf-section-title">
              Use every door. <span className="sf-byok-grad">For any kind of writing.</span> No charge.
            </h2>
            <p className="sf-section-sub">
              Scribe Flow Studio is built to give every creator a place to work, explore, and grow —
              without paywalls, trials, or locked features standing in the way.
            </p>
          </div>

          <div className="sf-byok-grid">
            <div className="sf-byok-card sf-byok-free">
              <div className="sf-byok-card-tag sf-byok-tag-free">Always Free</div>
              <h3>Built into Scribe Flow</h3>
              <p className="sf-byok-card-intro">
                These tools are included for everyone, with no setup and no cost:
              </p>
              <ul>
                <li>Manuscript analysis & feedback</li>
                <li>Creative brainstorming & character builder</li>
                <li>Polish, rewrite, and structural guidance</li>
                <li>DOCX / PDF / TXT import & export</li>
                <li>Publisher search & query letter generation</li>
                <li>Soulful chat companion at every step</li>
              </ul>
              <p className="sf-byok-note">
                These run on AI we provide. You don't need anything — just walk in.
              </p>
            </div>

            <div className="sf-byok-card sf-byok-byok">
              <div className="sf-byok-card-tag sf-byok-tag-byok">Bring Your Own Key</div>
              <h3>Premium AI Features</h3>
              <p className="sf-byok-card-intro">
                A few advanced tools tap into external AI engines.
                To use them, you bring your own API key — and you only pay for what you generate.
              </p>
              <ul>
                <li><strong>Audiobook Studio</strong> — 9 voices, multi-chapter narration</li>
                <li><strong>Cover Art Studio</strong> — KDP-ready 300 DPI book covers, album art</li>
                <li><strong>Deep Translation</strong> — 12 languages, soul intact</li>
              </ul>
              <p className="sf-byok-note">
                These connect directly to OpenAI. You pay them — pennies per use — never us.
                Your wallet, your control.
              </p>
            </div>
          </div>

          {/* What is an API key? */}
          <div className="sf-byok-explainer">
            <h3 className="sf-byok-h3">
              <Star size={18} /> What's an API key, in plain English?
            </h3>
            <p>
              An <strong>API key</strong> is a small code that gives Scribe Flow permission to tap
              into powerful AI engines when you choose to use them. Think of it like turning on
              the <em>"pro power"</em> switch inside the studio.
            </p>
            <p>
              It doesn't give access to your personal account or private data — it simply tells
              the AI, <em>"This creative work belongs to you."</em>
            </p>
            <p>
              Most of Scribe Flow works instantly with no setup at all. Your key is only needed
              for a few premium features that rely on external AI horsepower.
            </p>

            <div className="sf-byok-pillars">
              <div className="sf-byok-pillar">
                <div className="sf-byok-pillar-num">01</div>
                <strong>You stay in charge.</strong>
              </div>
              <div className="sf-byok-pillar">
                <div className="sf-byok-pillar-num">02</div>
                <strong>Scribe Flow handles the complexity.</strong>
              </div>
              <div className="sf-byok-pillar">
                <div className="sf-byok-pillar-num">03</div>
                <strong>Your creativity gets the best tools available — when you want them.</strong>
              </div>
            </div>

            {/* Transparency */}
            <div className="sf-byok-transparency">
              <h4 className="sf-byok-section-h">
                <Eye size={16} /> Why do we work this way? (Transparency First)
              </h4>
              <p className="sf-trans-lede">Because creators deserve honesty.</p>
              <p>
                Most platforms hide AI usage behind subscriptions, credits, or inflated pricing.
                Scribe Flow does the opposite.
              </p>
              <ul className="sf-trans-list">
                <li>You pay OpenAI directly — we never add fees or markups</li>
                <li>You see real-time cost estimates before you generate anything</li>
                <li>You set your own spending limits</li>
                <li>You stay in full control of your budget at all times</li>
                <li>You only pay for what you actually create — nothing more</li>
              </ul>
              <p className="sf-trans-close">
                This keeps everything simple, fair, and transparent.
                <strong> No surprises. No hidden charges. No "mystery billing."</strong>
                <br />
                Just a powerful studio that respects your wallet as much as your work.
              </p>
            </div>

            {/* Real-world costs */}
            <div className="sf-byok-costs">
              <h4 className="sf-byok-section-h">Real-world costs (you'll see live estimates inside)</h4>
              <ul>
                <li><span>Audiobook of a 50,000-word novel</span><strong>$1 – $3</strong></li>
                <li><span>One KDP-ready book cover image</span><strong>$0.04 – $0.17</strong></li>
                <li><span>Translating a full chapter</span><strong>pennies</strong></li>
              </ul>
            </div>

            {/* Setup steps */}
            <div className="sf-byok-steps">
              <h4 className="sf-byok-section-h">Setting up your OpenAI key (5 minutes, one time)</h4>
              <ol>
                <li>
                  <span className="sf-step-num">1</span>
                  <div>
                    <strong>Create a free OpenAI account</strong>
                    <p>Sign up with your email at <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a></p>
                  </div>
                </li>
                <li>
                  <span className="sf-step-num">2</span>
                  <div>
                    <strong>Add a payment method (set a spending cap)</strong>
                    <p>Billing → Add payment method. We recommend setting a $10–$20 monthly cap so you can't be surprised.</p>
                  </div>
                </li>
                <li>
                  <span className="sf-step-num">3</span>
                  <div>
                    <strong>Create your secret key</strong>
                    <p>Go to "API keys" → "Create new secret key" → name it "Scribe Flow" → copy the key (it starts with <code>sk-</code>).</p>
                  </div>
                </li>
                <li>
                  <span className="sf-step-num">4</span>
                  <div>
                    <strong>Paste it into Scribe Flow</strong>
                    <p>You'll see a key field on the audiobook and cover art tools. Paste, save, and you're set. We never store it long-term.</p>
                  </div>
                </li>
              </ol>
            </div>

            <div className="sf-byok-providers">
              <h4 className="sf-byok-section-h">Where the magic comes from</h4>
              <p className="sf-byok-providers-sub">
                We connect to the absolute best AI providers in the world — the same ones the
                household-name apps use.
              </p>
              <div className="sf-providers-grid">
                <a href="https://platform.openai.com/" target="_blank" rel="noopener noreferrer" className="sf-provider-card">
                  <div className="sf-provider-name">OpenAI</div>
                  <div className="sf-provider-role">Audiobook narration · Cover art · Premium translation</div>
                  <div className="sf-provider-link">platform.openai.com ↗</div>
                </a>
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="sf-provider-card sf-provider-action">
                  <div className="sf-provider-name">Get Your Key</div>
                  <div className="sf-provider-role">Direct link to the OpenAI key page</div>
                  <div className="sf-provider-link">Create API key ↗</div>
                </a>
              </div>
            </div>

            {/* Security & Privacy */}
            <div className="sf-byok-security">
              <h4 className="sf-byok-section-h">
                <Lock size={16} /> Security & Privacy
              </h4>
              <p className="sf-sec-lede">Your API key stays yours.</p>
              <p>
                Scribe Flow never stores it long-term, never shares it, and never uses it for
                anything except the creative actions you choose.
              </p>
              <div className="sf-sec-pledges">
                <span><CheckCircle size={14} /> Your work stays private.</span>
                <span><CheckCircle size={14} /> Your costs stay transparent.</span>
                <span><CheckCircle size={14} /> Your creativity stays yours.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ REVIEWS / RATINGS ============ */}
      <ReviewsSection />

      {/* ============ ETHICS / TRUST ============ */}
      <section className="sf-section sf-ethics">
        <div className="sf-section-inner sf-ethics-inner">
          <h2 className="sf-section-title">Ethical AI. Real Results.</h2>
          <p>
            Every AiQ Phoenix Tech tool is built with integrity at its core. Our AI enhances
            your work without fabricating, exaggerating, or misleading. What you see is what
            you get — authentic outputs powered by decades of corporate experience.
          </p>
        </div>
      </section>

      {/* ============ WAITLIST ============ */}
      <section className="sf-section sf-waitlist" id="waitlist">
        <div className="sf-section-inner sf-waitlist-inner">
          <div className="sf-section-eyebrow">Founding Members</div>
          <h2 className="sf-section-title">Join the founding circle.</h2>
          <p className="sf-section-sub">
            When pricing opens, founding members get lifetime access at our lowest price ever.
            We'll only email when something matters.
          </p>

          {waitlistState.status === 'success' ? (
            <div className="sf-waitlist-success" data-testid="waitlist-success">
              <CheckCircle size={28} />
              <h3>You're #{waitlistState.position} in line.</h3>
              <p>{waitlistState.message}</p>
            </div>
          ) : (
            <form className="sf-waitlist-form" onSubmit={joinWaitlist}>
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={e => setName(e.target.value)}
                className="sf-waitlist-input"
                data-testid="waitlist-name"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="sf-waitlist-input"
                data-testid="waitlist-email"
              />
              <button
                type="submit"
                className="sf-cta-btn sf-cta-btn-primary"
                disabled={waitlistState.status === 'loading'}
                data-testid="waitlist-submit"
              >
                {waitlistState.status === 'loading' ? (
                  <><Loader2 className="spinning" size={16} /> Joining...</>
                ) : (
                  <><Bell size={16} /> Reserve My Spot</>
                )}
              </button>
            </form>
          )}

          {waitlistState.status === 'error' && (
            <p className="sf-waitlist-error">{waitlistState.message}</p>
          )}
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="sf-footer">
        <div className="sf-footer-inner">
          <div className="sf-footer-brand">
            <Flame />
            <div>
              <p className="sf-footer-parent">AiQ Phoenix Tech</p>
              <p className="sf-footer-tag">Where bold ideas ignite powerful, dependable tech.</p>
            </div>
          </div>
          <div className="sf-footer-links">
            <a href="https://aiquires-platform-production.up.railway.app/" target="_blank" rel="noopener noreferrer">
              AiQ Phoenix Tech ↗
            </a>
            <a href="https://aiquires-platform-production.up.railway.app/" target="_blank" rel="noopener noreferrer">
              Resume Optimizer ↗
            </a>
            <button className="sf-footer-link-btn" onClick={onEnterStudio}>Enter the Studio</button>
          </div>
          <p className="sf-footer-powered">Powered by AiQuireS and JBTechnology</p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
